/**
 * LeftShield Admin - Home Page (Platform Dashboard)
 * Overview of platform metrics and recent activity
 */
import React from 'react';
import {
  Title1,
  Title2,
  Body1,
  Body2,
  Card,
  CardHeader,
  Badge,
  tokens,
  Spinner,
} from '@fluentui/react-components';
import {
  BuildingRegular,
  PersonRegular,
  DocumentSearchRegular,
  BugRegular,
  ClockRegular,
  ArrowTrendingRegular,
} from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { metricsApi, trialsApi } from '../services/api';

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: number;
  color?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, icon, trend, color }) => (
  <Card style={{ minWidth: '200px' }}>
    <CardHeader
      image={<div style={{ color: color || tokens.colorBrandForeground1, fontSize: '1.5rem' }}>{icon}</div>}
      header={<Body2>{title}</Body2>}
      description={
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Title2 style={{ color: color }}>{value}</Title2>
          {trend !== undefined && (
            <Badge
              appearance="tint"
              color={trend >= 0 ? 'success' : 'danger'}
              icon={<ArrowTrendingRegular />}
            >
              {trend >= 0 ? '+' : ''}{trend}%
            </Badge>
          )}
        </div>
      }
    />
  </Card>
);

export const HomePage: React.FC = () => {
  const { data: metrics, isLoading: metricsLoading } = useQuery({
    queryKey: ['platformMetrics'],
    queryFn: () => metricsApi.getPlatformMetrics(),
  });

  const { data: trials, isLoading: trialsLoading } = useQuery({
    queryKey: ['trials'],
    queryFn: () => trialsApi.getAll(false),
  });

  const isLoading = metricsLoading || trialsLoading;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <Spinner size="large" label="Loading dashboard..." />
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem' }}>
      <Title1 style={{ marginBottom: '1.5rem' }}>Platform Dashboard</Title1>

      {/* Platform Metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        <MetricCard
          title="Total Tenants"
          value={metrics?.totalTenants ?? 0}
          icon={<BuildingRegular />}
        />
        <MetricCard
          title="Active Tenants"
          value={metrics?.activeTenants ?? 0}
          icon={<BuildingRegular />}
          color={tokens.colorPaletteGreenForeground1}
        />
        <MetricCard
          title="Total Users"
          value={metrics?.totalUsers ?? 0}
          icon={<PersonRegular />}
        />
        <MetricCard
          title="Total Scans"
          value={metrics?.totalScans ?? 0}
          icon={<DocumentSearchRegular />}
        />
        <MetricCard
          title="Total Findings"
          value={metrics?.totalFindings ?? 0}
          icon={<BugRegular />}
          color={tokens.colorPaletteRedForeground1}
        />
        <MetricCard
          title="API Requests Today"
          value={metrics?.apiRequestsToday ?? 0}
          icon={<ArrowTrendingRegular />}
        />
      </div>

      {/* Trial Summary */}
      <Card style={{ marginBottom: '2rem' }}>
        <CardHeader
          image={<ClockRegular style={{ fontSize: '1.5rem', color: tokens.colorBrandForeground1 }} />}
          header={<Title2>Trial Overview</Title2>}
          description={<Body2>Active and expiring trials</Body2>}
        />
        <div style={{ padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
          <div style={{ textAlign: 'center' }}>
            <Title1>{trials?.totalTrials ?? 0}</Title1>
            <Body1>Active Trials</Body1>
          </div>
          <div style={{ textAlign: 'center' }}>
            <Title1 style={{ color: tokens.colorPaletteYellowForeground1 }}>
              {trials?.expiringIn7Days ?? 0}
            </Title1>
            <Body1>Expiring in 7 Days</Body1>
          </div>
          <div style={{ textAlign: 'center' }}>
            <Title1 style={{ color: tokens.colorPaletteRedForeground1 }}>
              {trials?.expiredCount ?? 0}
            </Title1>
            <Body1>Expired</Body1>
          </div>
        </div>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader
          header={<Title2>Quick Actions</Title2>}
          description={<Body2>Common administrative tasks</Body2>}
        />
        <div style={{ padding: '1rem' }}>
          <Body1 style={{ color: tokens.colorNeutralForeground3 }}>
            Use the navigation menu to access tenant management, trial creation, and platform settings.
          </Body1>
        </div>
      </Card>
    </div>
  );
};
